/**
 * An outfit as one picture.
 *
 * Brian, 12 Aug 2026, on the Plan tab: "I've got a one quarter view right pane of outfits that
 * are not clickable, that are not filterable, they're not ordered... if I wanted to say pick
 * Thursday's outfit, I don't know what to choose because there's no images or filtering on the
 * right hand pane."
 *
 * The right pane listed 30 outfits by name. Names generated by a model are not distinguishable
 * at a glance, so choosing meant opening each one. This renders the garments instead.
 *
 * A tile per slot in layer order, so the same outfit always reads the same way top to bottom:
 * outer, mid, base, bottom, shoes, accessory. A slot that is a WANT rather than an owned garment
 * is a dashed box, not a hole — that distinction is the whole point of a list where an outfit can
 * legitimately be missing three things.
 */
import { useState } from 'react';
import type { Outfit, OutfitSlot, GarmentLayer } from '../../shared/types';
import { cx, photoSrc } from './ui';

const LAYER_ORDER: GarmentLayer[] = ['outer', 'mid', 'base', 'bottom', 'shoes', 'accessory'];

function ordered(slots: OutfitSlot[]): OutfitSlot[] {
  return [...slots].sort(
    (a, b) => LAYER_ORDER.indexOf(a.slot) - LAYER_ORDER.indexOf(b.slot)
  );
}

/** One garment. Photo if there is one, a labelled box if not. */
function SlotTile({ slot, size }: { slot: OutfitSlot; size: 'sm' | 'md' }) {
  const [broken, setBroken] = useState(false);
  const owned = slot.item_id !== null;
  const show = owned && slot.has_image && !broken;
  const label = slot.item_name ?? slot.gap_description ?? slot.slot;
  const box = size === 'sm' ? 'h-11 w-11' : 'h-16 w-16';

  return (
    <span
      title={owned ? label : `Need: ${label}`}
      className={cx(
        box,
        'relative block shrink-0 overflow-hidden rounded border',
        owned ? 'border-border bg-surface-sunk' : 'border-dashed border-clay/60 bg-clay-soft'
      )}
    >
      {show ? (
        <img
          src={photoSrc({ id: slot.item_id!, image_r2_key: slot.image_r2_key })}
          alt={label}
          loading="lazy"
          onError={() => setBroken(true)}
          className="h-full w-full object-cover"
        />
      ) : (
        // No photo, or the object is missing. Either way say WHAT it is rather than drawing an
        // empty square — the category is the most useful three letters available here.
        <span
          className={cx(
            'flex h-full w-full items-center justify-center px-0.5 text-center leading-none',
            // 9px was unreadable on the day cards, which are the ones Brian called tiny.
            size === 'sm' ? 'text-[9px]' : 'text-[11px] font-medium',
            owned ? 'text-ink-soft' : 'text-clay'
          )}
        >
          {slot.item_category ?? slot.slot}
        </span>
      )}
    </span>
  );
}

export default function OutfitThumb({
  outfit,
  size = 'sm',
}: {
  outfit: Outfit;
  size?: 'sm' | 'md';
}) {
  const slots = ordered(outfit.slots);
  if (slots.length === 0) {
    return <p className="text-xs text-ink-soft italic">no garments recorded</p>;
  }
  return (
    <span className="flex flex-wrap gap-1">
      {slots.map((s) => (
        <SlotTile key={`${s.slot}-${s.item_id ?? s.gap_id ?? 'x'}`} slot={s} size={size} />
      ))}
    </span>
  );
}
